/**
 * Platform admin → self-service sign-up review queue.
 *
 * Backed by /v1/admin/signup-requests/* — the API gates these to admins of
 * the PLATFORM tenant (a reseller admin gets 403), so the sidebar link is
 * shown only for tenant_type='platform'. Approve provisions + activates the
 * tenant from the pending request; reject marks it declined.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '@/lib/api';

export interface SignupRequest {
  id: number;
  full_name: string;
  email: string;
  company: string;
  phone: string;
  country: string;
  status: string;
  tenant_id?: number | null;
  created_at: string;
}

interface SignupListResponse {
  items: SignupRequest[];
  total: number;
}

const SIGNUPS_KEY = ['admin', 'signup-requests'] as const;

export function useSignupRequests(opts: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: SIGNUPS_KEY,
    queryFn: () => api.get<SignupListResponse>('/v1/admin/signup-requests'),
    enabled: opts.enabled ?? true,
    // The queue is a low-frequency human workflow; a gentle poll keeps it
    // fresh if a request lands while the admin has the tab open.
    refetchInterval: 60_000,
  });
}

export function useApproveSignup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      api.post<{ status: string; tenant_id: number }>(`/v1/admin/signup-requests/${id}/approve`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: SIGNUPS_KEY });
      // A new tenant now exists — refresh the customers list too.
      void qc.invalidateQueries({ queryKey: ['admin', 'customers'] });
    },
  });
}

export function useRejectSignup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: number; note?: string }) =>
      api.post<{ status: string }>(`/v1/admin/signup-requests/${vars.id}/reject`, {
        note: vars.note ?? '',
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: SIGNUPS_KEY });
    },
  });
}
